import { getSoftlockClient } from './core';
import { SoftlockMiddlewareOptions, ValidationResult } from './types';

/**
 * Express middleware for protecting routes with Softlock access keys
 */
export function createExpressMiddleware(options: SoftlockMiddlewareOptions = {}) {
  return async (req: any, res: any, next: any) => {
    try {
      // Extract access key from request
      let accessKey: string | null = null;

      if (options.extractKey) {
        accessKey = options.extractKey(req);
      } else {
        // Default extraction methods
        accessKey =
          req.headers['x-access-key'] ||
          req.headers['authorization']?.replace('Bearer ', '') ||
          req.query.access_key ||
          req.body?.access_key ||
          null;
      }

      if (!accessKey) {
        if (options.onUnauthorized) {
          return options.onUnauthorized(req, res);
        }
        return res.status(401).json({
          error: 'Access key required',
          code: 'MISSING_ACCESS_KEY',
        });
      }

      // Validate the access key
      const client = getSoftlockClient();
      const result: ValidationResult = await client.validateKey(accessKey);

      if (!result.valid) {
        if (options.onUnauthorized) {
          return options.onUnauthorized(req, res);
        }
        return res.status(403).json({
          error: result.error || 'Invalid access key',
          code: 'INVALID_ACCESS_KEY',
        });
      }

      // Add validation result to request object
      req.softlock = {
        valid: true,
        key: result.key,
        user: result.key ? {
          discordId: result.key.discord_user_id,
          discordTag: result.key.discord_tag,
        } : undefined,
      };

      next();
    } catch (error) {
      if (options.onError) {
        return options.onError(
          error instanceof Error ? error.message : 'Unknown error',
          req,
          res
        );
      }

      return res.status(500).json({
        error: 'Internal server error',
        code: 'VALIDATION_ERROR',
      });
    }
  };
}

/**
 * Next.js middleware for protecting routes with Softlock access keys
 */
export function createNextMiddleware(options: SoftlockMiddlewareOptions = {}) {
  return async (request: any) => {
    try {
      // Extract access key from request
      let accessKey: string | null = null;

      if (options.extractKey) {
        accessKey = options.extractKey(request);
      } else {
        // Default extraction methods for Next.js
        accessKey =
          request.headers.get('x-access-key') ||
          request.headers.get('authorization')?.replace('Bearer ', '') ||
          request.nextUrl.searchParams.get('access_key') ||
          null;
      }

      if (!accessKey) {
        return new Response(
          JSON.stringify({
            error: 'Access key required',
            code: 'MISSING_ACCESS_KEY',
          }),
          {
            status: 401,
            headers: { 'Content-Type': 'application/json' },
          }
        );
      }

      // Validate the access key
      const client = getSoftlockClient();
      const result: ValidationResult = await client.validateKey(accessKey);

      if (!result.valid) {
        return new Response(
          JSON.stringify({
            error: result.error || 'Invalid access key',
            code: 'INVALID_ACCESS_KEY',
          }),
          {
            status: 403,
            headers: { 'Content-Type': 'application/json' },
          }
        );
      }

      // Add validation result to request headers for downstream handlers
      const requestHeaders = new Headers(request.headers);
      requestHeaders.set('x-softlock-valid', 'true');
      requestHeaders.set('x-softlock-user-id', result.key?.discord_user_id || '');
      requestHeaders.set('x-softlock-user-tag', result.key?.discord_tag || '');
      requestHeaders.set('x-softlock-key-id', result.key?.id || '');

      // For Next.js middleware, we need to return undefined to continue
      // The calling middleware should handle the modified headers
      return undefined;
    } catch (error) {
      return new Response(
        JSON.stringify({
          error: 'Internal server error',
          code: 'VALIDATION_ERROR',
        }),
        {
          status: 500,
          headers: { 'Content-Type': 'application/json' },
        }
      );
    }
  };
}

/**
 * Utility function to extract Softlock data from Next.js request headers
 */
export function getSoftlockDataFromHeaders(headers: Headers) {
  return {
    valid: headers.get('x-softlock-valid') === 'true',
    userId: headers.get('x-softlock-user-id') || undefined,
    userTag: headers.get('x-softlock-user-tag') || undefined,
    keyId: headers.get('x-softlock-key-id') || undefined,
  };
}

/**
 * Higher-order function for protecting API routes
 */
export function withSoftlockAuth(
  handler: (req: any, res: any) => Promise<any>,
  options: SoftlockMiddlewareOptions = {}
) {
  return async (req: any, res: any) => {
    const middleware = createExpressMiddleware(options);

    return new Promise((resolve, reject) => {
      middleware(req, res, (err: any) => {
        if (err) {
          reject(err);
        } else {
          resolve(handler(req, res));
        }
      });
    });
  };
}

// Type augmentation for Express
declare global {
  namespace Express {
    interface Request {
      softlock?: {
        valid: boolean;
        key?: {
          id: string;
          status: string;
          discord_user_id?: string;
          discord_tag?: string;
          created_at: string;
          expires_at?: string;
        };
        user?: {
          discordId?: string;
          discordTag?: string;
        };
      };
    }
  }
}
